import { useEffect, useState } from 'react';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HotelListOptions } from '@bandido/shared/types/hotels/hotel-search-results';

export function HotelResultControls({
  value,
  disabled,
  onChange,
}: {
  value: HotelListOptions;
  disabled: boolean;
  onChange: (options: HotelListOptions) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(value.name ?? '');
  useEffect(() => setName(value.name ?? ''), [value.name]);
  const priceSort = value.sort === 'price_asc' || value.sort === 'price_desc';
  return (
    <Stack gap={1} sx={{ mt: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }}>
        <Stack
          component="form"
          direction="row"
          gap={1}
          flex={1}
          onSubmit={(event) => {
            event.preventDefault();
            if (!disabled) onChange({ ...value, name: name.trim() || undefined });
          }}
        >
          <TextField
            type="search"
            size="small"
            fullWidth
            label={t('hotelSearch.hotelName')}
            value={name}
            disabled={disabled}
            inputProps={{ maxLength: 200 }}
            onChange={(event) => setName(event.target.value)}
          />
          <Button type="submit" disabled={disabled}>
            {t('hotelSearch.applyName')}
          </Button>
        </Stack>
        <TextField
          select
          size="small"
          label={t('hotelSearch.sortHotels')}
          value={value.sort ?? 'selection'}
          disabled={disabled}
          sx={{ minWidth: 220 }}
          onChange={(event) => {
            const sort = event.target.value as HotelListOptions['sort'];
            onChange({
              sort,
              name: value.name,
              ...(sort === 'price_asc' || sort === 'price_desc'
                ? { currency: 'USD', price_basis: value.price_basis ?? 'gross' }
                : {}),
            });
          }}
        >
          <MenuItem value="selection">{t('hotelSearch.sortSelection')}</MenuItem>
          <MenuItem value="price_asc">{t('hotelSearch.sortCheapest')}</MenuItem>
          <MenuItem value="price_desc">{t('hotelSearch.sortHighest')}</MenuItem>
        </TextField>
        {priceSort && (
          <TextField
            select
            size="small"
            label={t('hotelSearch.priceBasis')}
            value={value.price_basis ?? 'gross'}
            disabled={disabled}
            sx={{ minWidth: 140 }}
            onChange={(event) => onChange({ ...value, price_basis: event.target.value as 'gross' | 'net' })}
          >
            <MenuItem value="gross">{t('hotelSearch.basis.gross')}</MenuItem>
            <MenuItem value="net">{t('hotelSearch.basis.net')}</MenuItem>
          </TextField>
        )}
      </Stack>
      <Typography variant="caption">{t(priceSort ? 'hotelSearch.sortPriceScope' : 'hotelSearch.nameScope')}</Typography>
    </Stack>
  );
}
